import { isSerialNumberResult, isSparePartListResult } from '@ecs-services/search/search.helper';
import { SerialNumberSearchResult, SparePartListSearchResult } from '@ecs-services/search/search.interface';
import { ActionCreator, Creator, ReducerTypes, on } from '@ngrx/store';
import { OnReducer, reducerFilter } from '../../../store.helper';
import { SearchState } from '../../search.reducer';
import * as ProductPageActions from './product-page.actions';

const productPageStateReducer =
    <Creators extends readonly ActionCreator[]>(
        reducer: OnReducer<SearchState<SerialNumberSearchResult | SparePartListSearchResult>, Creators>
    ): OnReducer<SearchState, Creators> =>
    (state: SearchState, action) =>
        reducerFilter(
            (
                unfilteredState: SearchState
            ): unfilteredState is SearchState<SerialNumberSearchResult | SparePartListSearchResult> =>
                isSerialNumberResult(unfilteredState.searchResult) ||
                isSparePartListResult(unfilteredState.searchResult),
            reducer
        )(state, action);

export const productPageReducers: ReducerTypes<
    SearchState,
    readonly ActionCreator<string, Creator<any[], any>>[]
>[] = [
    on(
        ProductPageActions.receiveProductPicture,
        productPageStateReducer((state, { payload: { pictureUrl } }) => ({
            ...state,
            searchResult: {
                ...state.searchResult,
                pictureUrl,
            },
        }))
    ),

    on(
        ProductPageActions.receiveSpareParts,
        productPageStateReducer((state, { payload: { spareParts } }) => ({
            ...state,
            searchResult: {
                ...state.searchResult,
                spareParts,
                sparePartsError: null,
            },
        }))
    ),

    on(
        ProductPageActions.receiveSparePartsWear,
        productPageStateReducer((state, { payload: { sparePartsWithWear } }) => ({
            ...state,
            searchResult: {
                ...state.searchResult,
                ...(state.searchResult.spareParts
                    ? {
                          spareParts: {
                              ...state.searchResult.spareParts,
                              groups: state.searchResult.spareParts.groups.map((group) => ({
                                  ...group,
                                  spareParts: group.spareParts?.map((sparePart) => ({
                                      ...sparePart,
                                      wear: group.driveGroup
                                          ? (sparePartsWithWear[group.driveGroup].find(
                                                (sparePartWitWear) => sparePartWitWear.id === sparePart.id
                                            )?.wear ?? false)
                                          : false,
                                  })),
                              })),
                          },
                      }
                    : {}),
            },
        }))
    ),

    on(
        ProductPageActions.receiveSparePartsError,
        productPageStateReducer((state, { payload: { sparePartsError } }) => ({
            ...state,
            searchResult: {
                ...state.searchResult,
                spareParts: null,
                sparePartsError,
            },
        }))
    ),

    on(
        ProductPageActions.receiveSparePartsAvailability,
        productPageStateReducer((state, { payload: { sparePartsWithAvailability } }) => ({
            ...state,
            searchResult: {
                ...state.searchResult,
                ...(state.searchResult.spareParts
                    ? {
                          spareParts: {
                              ...state.searchResult.spareParts,
                              groups: state.searchResult.spareParts.groups.map((group) => ({
                                  ...group,
                                  spareParts: group.spareParts?.map((sparePart) => ({
                                      ...sparePart,
                                      availability: sparePartsWithAvailability.find(
                                          (sparePartWithAvailability) =>
                                              sparePartWithAvailability.sparePartId === sparePart.id
                                      )?.availability,
                                  })),
                              })),
                          },
                      }
                    : {}),
            },
        }))
    ),

    on(
        ProductPageActions.receiveSparePartRecommendations,
        productPageStateReducer((state, { payload: { recommendations } }) => ({
            ...state,
            searchResult: {
                ...state.searchResult,
                ...(state.searchResult.spareParts
                    ? {
                          spareParts: {
                              ...state.searchResult.spareParts,
                              groups: state.searchResult.spareParts.groups.map((group) => ({
                                  ...group,
                                  spareParts: group.spareParts?.map((sparePart) => ({
                                      ...sparePart,
                                      recommendation: recommendations.find(
                                          (recommendationGroup) =>
                                              recommendationGroup.group === group.title &&
                                              recommendationGroup.id === sparePart.id &&
                                              recommendationGroup.itemNumber === sparePart.itemNumber
                                      ),
                                  })),
                              })),
                          },
                      }
                    : {}),
            },
        }))
    ),

    on(
        ProductPageActions.receiveProductLifeCycleStatus,
        productPageStateReducer((state, { payload: productLifeCycleStatus }) => ({
            ...state,
            ...productLifeCycleStatus,
        }))
    ),

    on(
        ProductPageActions.receiveProductReconstructionStatus,
        productPageStateReducer((state, { payload: reconstruction }) => ({
            ...state,
            ...reconstruction,
        }))
    ),

    on(
        ProductPageActions.receiveProductPackageInformation,
        productPageStateReducer((state, { payload: packageInformation }) => ({
            ...state,
            searchResult: {
                ...state.searchResult,
                ...packageInformation,
            },
        }))
    ),
];
